import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import TeamLogo from "@/components/base/TeamLogo";
import MatchCard from "@/components/feature/MatchCard";
import NewsCard from "@/components/feature/NewsCard";
import LiveDemo from "./components/LiveDemo";
import ShareButton from "./components/ShareButton";
import { matches } from "@/mocks/matches";
import { newsList } from "@/mocks/news";
import { getTeam, formatDateCN, statusMap } from "@/utils";
import type { MatchEvent } from "@/types";

const eventIcon: Record<MatchEvent["type"], string> = {
  score: "ri-basketball-line",
  foul: "ri-alarm-warning-line",
  timeout: "ri-pause-circle-line",
  sub: "ri-exchange-line",
  info: "ri-information-line",
};

function NotFoundState() {
  return (
    <div className="w-full px-4 md:px-6 py-20 flex flex-col items-center text-center">
      <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-5">
        <i className="ri-error-warning-line"></i>
      </span>
      <h1 className="font-heading text-2xl font-bold text-foreground-950">赛事详情无法找到</h1>
      <p className="mt-2 text-sm text-foreground-500 max-w-md">该比赛可能已结束或链接有误，请返回直播或赛程页面重新选择。</p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <Link to="/live" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-live-line"></i> 查看直播列表
        </Link>
        <Link to="/schedule" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-300 text-foreground-900 text-sm font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-calendar-line"></i> 查看赛程
        </Link>
      </div>
    </div>
  );
}

export default function MatchDetail() {
  const { id } = useParams();
  const match = matches.find((m) => m.id === id);

  useSeo({
    title: match
      ? `${getTeam(match.home).name} VS ${getTeam(match.away).name} - NBA直播吧赛事详情`
      : "赛事详情无法找到 - NBA直播吧",
    description: match
      ? `${getTeam(match.home).name} 对阵 ${getTeam(match.away).name}，${formatDateCN(match.date)} ${match.time} 在${match.venue}进行，${match.stage}。查看实时赛况、比分与赛事资讯。`
      : "抱歉，该赛事详情无法找到。",
    keywords: match
      ? `${getTeam(match.home).short},${getTeam(match.away).short},NBA直播,NBA比分,NBA赛况`
      : "NBA直播,NBA赛事详情",
  });

  if (!match) return <NotFoundState />;

  const home = getTeam(match.home);
  const away = getTeam(match.away);
  const hasScore = match.status !== "upcoming";

  const sameDay = matches.filter((m) => m.date === match.date && m.id !== match.id);

  const relatedNews = (() => {
    const keys = [home.short, away.short, home.city, away.city, home.name, away.name];
    const matched = newsList.filter((n) => keys.some((k) => n.title.includes(k) || n.summary.includes(k)));
    const merged = [...matched, ...newsList.filter((n) => !matched.includes(n))];
    return merged.slice(0, 3);
  })();

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="flex items-center gap-2 text-xs text-foreground-400 font-label mb-3 flex-wrap">
        <i className="ri-home-4-line"></i>
        <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/live" className="hover:text-primary-600 cursor-pointer">NBA直播</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-600">{home.short} vs {away.short}</span>
      </div>

      <div className="bg-primary-950 rounded-xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 md:px-6 py-3 border-b border-primary-800">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-full bg-background-50/10 text-background-50 text-xs font-label">{match.stage}</span>
            <span className="px-2.5 py-1 rounded-full bg-background-50/10 text-background-50 text-xs font-label">{match.region}赛区</span>
          </div>
          {match.status === "live" ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-accent-950 text-xs font-label font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-950 animate-pulse-dot"></span> 直播中
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-background-50/10 text-background-50 text-xs font-label">{statusMap[match.status].label}</span>
          )}
        </div>

        <div className="px-4 md:px-6 py-7">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col items-center gap-3 flex-1">
              <TeamLogo teamId={match.home} size="lg" />
              <span className="font-heading text-base md:text-xl font-bold text-background-50 text-center">{home.name}</span>
              <span className="text-xs text-primary-300 font-label">主场 · {home.division}</span>
            </div>

            <div className="flex flex-col items-center px-2 md:px-6">
              {hasScore ? (
                <div className="flex items-center gap-3 md:gap-5 font-heading font-bold">
                  <span className="text-4xl md:text-6xl text-background-50">{match.homeScore}</span>
                  <span className="text-2xl md:text-3xl text-primary-500">:</span>
                  <span className="text-4xl md:text-6xl text-background-50">{match.awayScore}</span>
                </div>
              ) : (
                <span className="font-heading text-3xl md:text-5xl font-bold text-primary-400">VS</span>
              )}
              <span className="mt-3 text-xs md:text-sm text-primary-200 font-label whitespace-nowrap">
                {match.status === "live" ? `${match.period} ${match.clock}` : `${formatDateCN(match.date)} ${match.time}`}
              </span>
            </div>

            <div className="flex flex-col items-center gap-3 flex-1">
              <TeamLogo teamId={match.away} size="lg" />
              <span className="font-heading text-base md:text-xl font-bold text-background-50 text-center">{away.name}</span>
              <span className="text-xs text-primary-300 font-label">客场 · {away.division}</span>
            </div>
          </div>

          <p className="mt-6 text-sm text-primary-200 leading-relaxed text-center max-w-2xl mx-auto">{match.intro}</p>

          <div className="mt-6 flex justify-center">
            <ShareButton />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <LiveDemo match={match} />

          <div className="bg-background-50 rounded-lg border border-background-200">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-base font-bold text-foreground-950">实时赛况更新</h2>
            </div>
            {match.events.length === 0 ? (
              <div className="py-12 text-center text-foreground-400 flex flex-col items-center gap-2">
                <i className="ri-time-line text-3xl"></i>
                <p className="text-sm">比赛尚未开始，暂无实时赛况</p>
              </div>
            ) : (
              <ol className="p-4 md:p-5 flex flex-col gap-4">
                {match.events.map((e, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-lg">
                      <i className={eventIcon[e.type]}></i>
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-label text-sm font-semibold text-foreground-900">{e.time}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-600">{e.period}</span>
                      </div>
                      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{e.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-base font-bold text-foreground-950">比赛基本信息</h2>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-lg text-foreground-400 mt-0.5"></i>
                <div>
                  <dt className="text-xs text-foreground-500">比赛场馆</dt>
                  <dd className="text-sm text-foreground-900 font-medium">{match.venue}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-calendar-line text-lg text-foreground-400 mt-0.5"></i>
                <div>
                  <dt className="text-xs text-foreground-500">比赛时间</dt>
                  <dd className="text-sm text-foreground-900 font-medium">{formatDateCN(match.date)} {match.time}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-user-star-line text-lg text-foreground-400 mt-0.5"></i>
                <div>
                  <dt className="text-xs text-foreground-500">执法裁判</dt>
                  <dd className="text-sm text-foreground-900 font-medium">{match.referees.join("、")}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-trophy-line text-lg text-foreground-400 mt-0.5"></i>
                <div>
                  <dt className="text-xs text-foreground-500">比赛阶段</dt>
                  <dd className="text-sm text-foreground-900 font-medium">{match.stage}</dd>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div className="lg:col-span-1 flex flex-col gap-6">
          <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-base font-bold text-foreground-950">同日其他比赛</h2>
            </div>
            {sameDay.length === 0 ? (
              <p className="py-6 text-center text-sm text-foreground-400">当日暂无其他比赛</p>
            ) : (
              <div className="flex flex-col gap-3">
                {sameDay.map((m) => (
                  <MatchCard key={m.id} match={m} variant="row" />
                ))}
              </div>
            )}
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                <h2 className="font-heading text-base font-bold text-foreground-950">相关赛事资讯</h2>
              </div>
              <Link to="/news" className="text-xs text-foreground-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">更多</Link>
            </div>
            <div className="flex flex-col gap-4">
              {relatedNews.map((n) => (
                <NewsCard key={n.id} news={n} variant="compact" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}